import {
  DomainError,
  findMindmapNode,
  LIMITS,
  mindmapBranchToSnapshot,
  normalize,
  planBranch,
  planStorymapUpdate,
  type MindmapLinks,
  type MindmapNode,
  type StorymapLayout,
  type UpdateSummary,
} from '@blueprint/domain'
import { useEffect, useMemo, useState } from 'react'
import { Dialog } from '../../components/Dialog.js'
import { Button, Field, Select, TextInput } from '../../components/controls.js'
import { getRepository, useBoardStore } from '../../state/board-store.js'
import { useMindmapStore } from '../../state/mindmap-store.js'
import { useUiStore } from '../../state/ui-store.js'
import styles from './MindmapView.module.css'

/**
 * Generates a storymap from a branch (the root: the whole map). When boards were already
 * generated from this node, the user chooses between a new board and updating one of them.
 */
export function GenerateStorymapDialog({
  mindmapId,
  nodeId,
  root,
  boards,
  onClose,
  onDone,
}: {
  mindmapId: string
  nodeId: string
  root: MindmapNode
  /** Boards generated from this node that the user may edit. */
  boards: MindmapLinks['boards']
  onClose: () => void
  onDone: () => void
}) {
  const node = findMindmapNode(root, nodeId)
  const [mode, setMode] = useState<'new' | 'update'>('new')
  const [layout, setLayout] = useState<StorymapLayout>('columns')
  const [name, setName] = useState(node?.title ?? '')
  const [targetId, setTargetId] = useState(boards[0]?.id ?? '')
  const [summary, setSummary] = useState<UpdateSummary | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // What the branch will make, and why it cannot be made, shown before anything is created.
  const preview = useMemo(() => {
    try {
      const plan = planBranch(root, nodeId, layout)
      return { plan, problem: null as string | null }
    } catch (problem) {
      return { plan: null, problem: problem instanceof Error ? problem.message : 'This branch cannot be turned into a storymap.' }
    }
  }, [root, nodeId, layout])

  // For an update, the counts come from the board as it is now.
  useEffect(() => {
    if (mode !== 'update' || !targetId) return
    let cancelled = false
    setSummary(null)
    void getRepository()
      .getBoard(targetId)
      .then((snapshot) => {
        if (cancelled) return
        setSummary(planStorymapUpdate(normalize(snapshot), root, nodeId).summary)
      })
      .catch((problem: unknown) => {
        if (!cancelled) setError(problem instanceof Error ? problem.message : 'That board could not be loaded.')
      })
    return () => {
      cancelled = true
    }
  }, [mode, targetId, root, nodeId])

  const submit = async () => {
    if (!preview.plan || busy) return
    setBusy(true)
    setError(null)
    try {
      // The board is generated from the saved map, so the link back resolves.
      await useMindmapStore.getState().flush()
      const boardStore = useBoardStore.getState()
      if (mode === 'new') {
        const trimmed = name.trim()
        if (!trimmed) {
          setError('A board name is required.')
          return
        }
        const { snapshot } = mindmapBranchToSnapshot({ mindmapId, root, nodeId, name: trimmed, layout })
        await useMindmapStore.getState().leave()
        const id = await boardStore.importSnapshot(snapshot)
        if (!id) throw new Error(useBoardStore.getState().error ?? 'The storymap could not be created.')
      } else {
        await useMindmapStore.getState().leave()
        await boardStore.openBoard(targetId)
        const state = useBoardStore.getState().entities
        if (!state) throw new Error(useBoardStore.getState().error ?? 'That board could not be opened.')
        const { result } = planStorymapUpdate(state, root, nodeId)
        if (result) useBoardStore.getState().run(() => result)
      }
      useUiStore.getState().setView('board')
      onDone()
    } catch (problem) {
      setError(problem instanceof DomainError || problem instanceof Error ? problem.message : 'Something went wrong.')
    } finally {
      setBusy(false)
    }
  }

  const counts = preview.plan
    ? `${preview.plan.groups.length} group${preview.plan.groups.length === 1 ? '' : 's'}, ` +
      `${preview.plan.columns.length} column${preview.plan.columns.length === 1 ? '' : 's'}, ` +
      `${preview.plan.levels.length} swimlane${preview.plan.levels.length === 1 ? '' : 's'} and ` +
      `${preview.plan.cards.length} card${preview.plan.cards.length === 1 ? '' : 's'}`
    : ''

  return (
    <Dialog
      open
      title={node && node.id === root.id ? 'Create storymap from mindmap' : 'Create storymap from branch'}
      onClose={onClose}
      onSubmit={() => void submit()}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!preview.plan || busy || (mode === 'update' && !targetId)}>
            {busy ? 'Working…' : mode === 'new' ? 'Create storymap' : 'Update storymap'}
          </Button>
        </>
      }
    >
      {preview.problem ? <p className={styles.dialogError}>{preview.problem}</p> : null}
      {boards.length > 0 ? (
        <fieldset className={styles.choice}>
          <legend>This branch already has a storymap</legend>
          <label>
            <input type="radio" name="generate-mode" checked={mode === 'new'} onChange={() => setMode('new')} /> Create a new board
          </label>
          <label>
            <input type="radio" name="generate-mode" checked={mode === 'update'} onChange={() => setMode('update')} /> Update an existing board
          </label>
        </fieldset>
      ) : null}

      {mode === 'new' ? (
        <>
          <Field label="Board name">
            {(id) => (
              <TextInput id={id} data-autofocus value={name} maxLength={LIMITS.title} onChange={(event) => setName(event.target.value)} />
            )}
          </Field>
          <fieldset className={styles.choice}>
            <legend>The first level of child nodes become</legend>
            <label>
              <input type="radio" name="generate-layout" checked={layout === 'columns'} onChange={() => setLayout('columns')} /> Columns, with
              cards from the next level (add groups by hand)
            </label>
            <label>
              <input type="radio" name="generate-layout" checked={layout === 'groups'} onChange={() => setLayout('groups')} /> Column groups,
              with columns from the next level and cards below them
            </label>
          </fieldset>
          {preview.plan ? <p className={styles.dialogText}>The new board will have {counts}.</p> : null}
          {preview.plan?.warnings.map((warning) => (
            <p key={warning} className={styles.dialogText}>
              {warning}
            </p>
          ))}
        </>
      ) : (
        <>
          {boards.length > 1 ? (
            <Field label="Board to update">
              {(id) => (
                <Select id={id} value={targetId} onChange={(event) => setTargetId(event.target.value)}>
                  {boards.map((board) => (
                    <option key={board.id} value={board.id}>
                      {board.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          ) : (
            <p className={styles.dialogText}>Board: {boards[0]?.name}</p>
          )}
          <p className={styles.dialogText}>
            {summary
              ? `${summary.updated} to update, ${summary.added} to add, ${summary.sourceRemoved} whose node is gone (kept, not deleted).`
              : 'Comparing with the board…'}
          </p>
          <p className={styles.dialogText}>
            Matched cards and headings take the node&apos;s text, note, status, colour and tags. A card moves only when its node&apos;s
            placement has changed. Anything added by hand is left alone.
          </p>
        </>
      )}
      {error ? <p className={styles.dialogError}>{error}</p> : null}
    </Dialog>
  )
}
